import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../AuthProvider';
import { useApp } from '../../AppProvider';
import api from './../../axiosInstance';
import Loader from '../GlobalLoader/Loader';
import logger from "../../utils/logger";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];


const INR = (n) => n == null ? "—" : "₹" + Math.round(n).toLocaleString("en-IN");
const INRd = (n) => n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PILL_COLORS = {
  "Pending": { background: "#fef3c7", color: "#92400e" },
  "Partially Paid": { background: "#dbeafe", color: "#1e40af" },
  "Paid": { background: "#dcfce7", color: "#166534" },
  "Cancelled": { background: "#f3f4f6", color: "#6b7280" },
};

// ─── Calendar ─────────────────────────────────────────────────────────────────
function Calendar({ year, month, selectedDate, onSelect, onPrevMonth, onNextMonth }) {
  const today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMo = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const trailing = (7 - ((firstDay + daysInMo) % 7)) % 7;

  const cells = [];
  for (let i = 0; i < firstDay; i++)
    cells.push({ day: prevDays - firstDay + 1 + i, type: "prev" });
  for (let d = 1; d <= daysInMo; d++)
    cells.push({ day: d, type: "cur" });
  for (let i = 1; i <= trailing; i++)
    cells.push({ day: i, type: "next" });

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isSelected = (d) => selectedDate?.d === d && selectedDate?.m === month && selectedDate?.y === year;
  const isPast = (d) => new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div style={S.calBox}>
      <div style={S.calNav}>
        <button onClick={onPrevMonth} style={S.calNavBtn}>‹</button>
        <span style={{ fontSize: 12, fontWeight: 500, color: "#1a2a3a" }}>{MONTHS[month]} {year}</span>
        <button onClick={onNextMonth} style={S.calNavBtn}>›</button>
      </div>
      <div style={S.calGrid}>
        {DAYS.map(d => <div key={d} style={S.calDayHdr}>{d}</div>)}
        {cells.map((c, i) => {
          if (c.type !== "cur") return <div key={i} style={S.calDayOther}>{c.day}</div>;
          const past = isPast(c.day) && !isToday(c.day);
          const sel = isSelected(c.day);
          const tod = isToday(c.day);
          return (
            <div key={i} onClick={() => !past && onSelect(c.day)} style={{
              ...S.calDay,
              ...(past && !tod ? S.calDayDisabled : {}),
              ...(tod && !sel ? S.calDayToday : {}),
              ...(sel ? S.calDaySelected : {}),
            }}>
              {c.day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TimeSlots ────────────────────────────────────────────────────────────────
function TimeSlots({ slots, selectedSlot, onSelect, loading }) {
  const fmt = (t) => `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
  return (
    <div style={{ ...S.slotsBox, minHeight: 130 }}>
      {loading ? (
        <div style={S.slotMsg}>Loading slots…</div>
      ) : !slots || slots.length === 0 ? (
        <div style={S.slotMsg}>Select a date to see available slots</div>
      ) : (
        <div style={S.slotsGrid}>
          {slots.map((slot, i) => (
            <div key={i} onClick={() => onSelect(i)}
              style={{ ...S.slot, ...(selectedSlot === i ? S.slotSel : {}) }}>
              {`${fmt(slot.startTime)} – ${fmt(slot.endTime)}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SkuRow ───────────────────────────────────────────────────────────────────
function SkuRow({ item, index, onQtyChange, onDiscChange, onRemove, disabled }) {
  const gross = item.unitPrice * item.qty;
  const disc = gross * (item.lineDisc || 0) / 100;
  const after = gross - disc;
  const taxRate = (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
  const total = after + after * taxRate / 100;

  return (
    <div style={S.skuRow}>
      <div style={{ padding: "8px 10px" }}>
        <div style={S.skuName}>{item.name || item.skuId}</div>
        <div style={S.skuCat}>{item.category}</div>
      </div>
      <div style={S.skuCell}>{INR(item.unitPrice)}</div>
      <div style={S.skuCell}>
        <input type="number" min="1" max="99" value={item.qty} disabled={disabled}
          onChange={e => onQtyChange(index, e.target.value)}
          style={{ ...S.numInp, width: 50, opacity: disabled ? 0.5 : 1 }} />
      </div>
      <div style={{ ...S.skuCell, display: "flex", alignItems: "center", gap: 3 }}>
        <input type="number" min="0" max="100" value={item.lineDisc} disabled={disabled}
          onChange={e => onDiscChange(index, e.target.value)}
          style={{ ...S.numInp, width: 46, opacity: disabled ? 0.5 : 1 }} />
        <span style={{ fontSize: 11, color: "#9ca3af" }}>%</span>
      </div>
      <div style={{ ...S.skuCell, fontWeight: 500 }}>{INR(total)}</div>
      <div style={{ padding: "0 4px" }}>
        {!disabled && (
          <button onClick={() => onRemove(index)} style={S.delBtn}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} style={{ ...S.togTrack, background: on ? "#1d9e75" : "#d1d5db" }}>
      <div style={{ ...S.togThumb, left: on ? 19 : 3 }} />
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const EditBookedAppointment = ({ setEditModalOpen, task }) => {
  const { user } = useAuth();
  const { selectedClinic, appointments, setAppointments } = useApp();

  const selectedAppointment = appointments.find(a => a._id.toString() === task.id.toString());
  const apptDateObj = new Date(selectedAppointment?.dateOfAppointment || Date.now());

  // Tabs — default to invoice when no appointment is linked
  const [activeTab, setActiveTab] = useState(selectedAppointment ? "appointment" : "invoice");

  // Appointment state
  const [calYear, setCalYear] = useState(apptDateObj.getFullYear());
  const [calMonth, setCalMonth] = useState(apptDateObj.getMonth());
  const [selectedDate, setSelectedDate] = useState({
    d: apptDateObj.getDate(),
    m: apptDateObj.getMonth(),
    y: apptDateObj.getFullYear(),
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [apptTypes, setApptTypes] = useState([]);
  const [apptType, setApptType] = useState("");
  const [savingAppt, setSavingAppt] = useState(false);
  const [deletingAppt, setDeletingAppt] = useState(false);
  const [completingAppt, setCompletingAppt] = useState(false);

  // Invoice state
  const [dataLoading, setDataLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [skuCatalog, setSkuCatalog] = useState([]);
  const [skuItems, setSkuItems] = useState([]);
  const [skuPickerId, setSkuPickerId] = useState("");
  const [insEnabled, setInsEnabled] = useState(false);
  const [insCompany, setInsCompany] = useState("");
  const [insPolicy, setInsPolicy] = useState("");
  const [insCoverage, setInsCoverage] = useState(0);
  const [savingInvoice, setSavingInvoice] = useState(false);

  // Payment recording state
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const [payNote, setPayNote] = useState("");
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Load SKU catalog + existing invoice in parallel
  useEffect(() => {
    const clinicId = selectedClinic?._id;
    if (!clinicId || !task.patientId) { setDataLoading(false); return; }

    setDataLoading(true);
    Promise.all([
      api.get("/clinicSKU", { params: { clinicId, limit: 1000, page: 1 } }),
      api.get(`/medicalExpenditures/${task.medicalExpenditureId}`),
      api.get("/appointmentTypes", { params: { clinicId, isActive: true, limit: 100, page: 1 } }),
    ])
      .then(([catRes, invRes, typesRes]) => {
        const catalog = (catRes.data.data || []).filter(s => s.isActive !== false);
        setSkuCatalog(catalog);

        const types = typesRes.data.data || [];
        setApptTypes(types);
        const currentName = selectedAppointment?.appointmentName || "";
        const matched = types.find(t => t.name === currentName);
        setApptType(matched?._id || (types[0]?._id ?? ""));

        const linked = invRes.data.data || undefined;


        if (linked) {
          setInvoice(linked);
          setSkuItems(
            (linked.expenseTable || []).map(e => {
              const sku = catalog.find(s => s.clinicSkuId === e.skuId || s._id.toString() === e.skuId);
              return {
                skuId: e.skuId,
                name: e.name || sku?.name || e.skuId,
                category: e.category || sku?.category || "",
                unitPrice: e.unitPrice ?? sku?.unitPrice ?? 0,
                cgst: sku?.tax?.cgst ?? 0,
                sgst: sku?.tax?.sgst ?? 0,
                igst: sku?.tax?.igst ?? 0,
                qty: e.quantity || 1,
                lineDisc: e.discount || 0,
              };
            })
          );
          if (linked.insuranceDetails?.insuranceCompany) {
            setInsEnabled(true);
            setInsCompany(linked.insuranceDetails.insuranceCompany);
            setInsPolicy(String(linked.insuranceDetails.policyNumber || ""));
            setInsCoverage(linked.insuranceDetails.coveragePercent || 0);
          }
        }
      })
      .catch(err => logger.error("Error loading data:", err))
      .finally(() => setDataLoading(false));
  }, [selectedClinic, task.patientId, task.id]);

  // Totals
  const totals = useMemo(() => {
    let subTotal = 0, lineDiscTotal = 0, taxTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
    for (const s of skuItems) {
      const gross = s.unitPrice * s.qty;
      const disc = gross * (s.lineDisc || 0) / 100;
      const after = gross - disc;
      subTotal += gross;
      lineDiscTotal += disc;
      cgstTotal += after * (s.cgst || 0) / 100;
      sgstTotal += after * (s.sgst || 0) / 100;
      igstTotal += after * (s.igst || 0) / 100;
      taxTotal += after * ((s.cgst || 0) + (s.sgst || 0) + (s.igst || 0)) / 100;
    }
    const afterLineDsc = subTotal - lineDiscTotal;
    const insAmt = insEnabled ? afterLineDsc * insCoverage / 100 : 0;
    const grand = afterLineDsc - insAmt + taxTotal;
    const paid = invoice?.totalPaidAmount ?? 0;
    const balance = Math.max(0, grand - paid);
    return { subTotal, lineDiscTotal, insAmt, taxTotal, cgstTotal, sgstTotal, igstTotal, grand, paid, balance };
  }, [skuItems, insEnabled, insCoverage, invoice]);

  // Calendar navigation
  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const handleDateSelect = async (day) => {
    setSelectedDate({ d: day, m: calMonth, y: calYear });
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const dateString = new Date(calYear, calMonth, day).toISOString().slice(0, 10);
      const res = await api.get(`/clinics/findEmptySlots/${selectedClinic._id}`, {
        params: { doctorId: user._id, date: dateString },
      });
      setTimeSlots(res.data.emptySlots || []);
    } catch (err) {
      logger.error("Error fetching slots:", err);
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // SKU actions
  const addSku = () => {
    if (!skuPickerId) return;
    const sku = skuCatalog.find(s => s.clinicSkuId === skuPickerId);
    if (!sku || skuItems.find(s => s.skuId === sku.clinicSkuId)) { setSkuPickerId(""); return; }
    setSkuItems(prev => [...prev, {
      skuId: sku.clinicSkuId,
      name: sku.name,
      category: sku.category || "",
      unitPrice: sku.unitPrice,
      cgst: sku.tax?.cgst ?? 0,
      sgst: sku.tax?.sgst ?? 0,
      igst: sku.tax?.igst ?? 0,
      qty: 1,
      lineDisc: 0,
    }]);
    setSkuPickerId("");
  };
  const removeSku = (i) => setSkuItems(prev => prev.filter((_, idx) => idx !== i));
  const updateQty = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, qty: Math.max(1, parseInt(v) || 1) } : s));
  const updateLineDsc = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, lineDisc: Math.min(100, Math.max(0, parseFloat(v) || 0)) } : s));

  const handleApptTypeChange = (typeId) => {
    setApptType(typeId);
    const type = apptTypes.find(t => t._id === typeId);
    if (type?.defaultSkus?.length > 0) {
      const items = type.defaultSkus.map(ds => {
        const skuId = String(ds.skuId?._id || ds.skuId || "");
        const sku = skuCatalog.find(s => String(s._id) === skuId);
        if (!sku) return null;
        return {
          skuId: sku.clinicSkuId,
          name: sku.name,
          category: sku.category || "",
          unitPrice: sku.unitPrice,
          cgst: sku.tax?.cgst ?? 0,
          sgst: sku.tax?.sgst ?? 0,
          igst: sku.tax?.igst ?? 0,
          qty: ds.qty || 1,
          lineDisc: ds.discount || 0,
        };
      }).filter(Boolean);
      setSkuItems(items);
    }
  };

  // Save appointment
  const saveAppointment = async () => {
    setSavingAppt(true);
    try {
      const { d, m, y } = selectedDate;
      let hour = apptDateObj.getHours();
      let minute = apptDateObj.getMinutes();
      if (selectedSlot !== null && timeSlots[selectedSlot]) {
        hour = timeSlots[selectedSlot].startTime.hour;
        minute = timeSlots[selectedSlot].startTime.minute;
      }
      const body = {
        clinicId: selectedClinic._id,
        doctorId: user._id,
        patientId: task.patientId,
        doctorName: `${user.name.firstName} ${user.name.lastName}`,
        patientName: task.content,
        clinicName: selectedClinic.name.fullName,
        appointmentName: apptTypes.find(t => t._id === apptType)?.name || apptType,
        dateOfAppointment: new Date(y, m, d, hour, minute).toISOString(),
        dateOfBooking: new Date().toISOString(),
        tags: apptTypes.find(t => t._id === apptType)?.name || apptType,
      };
      const res = await api.put(`/clinics/appointmentsandclinics/${task.id}`, body);
      setAppointments(prev => prev.map(a => a._id === task.id ? res.data : a));
      setEditModalOpen(false);
    } catch (err) {
      logger.error("Error saving appointment:", err);
    } finally {
      setSavingAppt(false);
    }
  };

  // Delete appointment
  const deleteAppointment = async () => {
    setDeletingAppt(true);
    try {
      await api.delete(`/appointments/${task.id}`);
      setAppointments(prev => prev.filter(a => a._id !== task.id));
      setEditModalOpen(false);
    } catch (err) {
      logger.error("Error deleting appointment:", err);
    } finally {
      setDeletingAppt(false);
    }
  };

  // Mark appointment as complete
  const markComplete = async () => {
    setCompletingAppt(true);
    try {
      await api.put(`/appointments/${task.id}`, { status: "COMPLETED" });
      setAppointments(prev => prev.map(a =>
        a._id === task.id ? { ...a, status: "COMPLETED" } : a
      ));
      setEditModalOpen(false);
    } catch (err) {
      logger.error("Error marking appointment complete:", err);
    } finally {
      setCompletingAppt(false);
    }
  };

  // Save / update invoice
  const saveInvoice = async () => {
    if (skuItems.length === 0) return;
    setSavingInvoice(true);
    try {
      const expenseTable = skuItems.map(s => ({
        skuId: s.skuId,
        quantity: s.qty,
        discount: s.lineDisc || 0,
      }));

      const body = { expenseTable };
      if (insEnabled) {
        body.insuranceDetails = {
          insuranceCompany: insCompany,
          policyNumber: Number(insPolicy) || insPolicy,
          coveragePercent: insCoverage,
        };
      }

      if (invoice) {
        const res = await api.put(`/medicalExpenditures/${invoice._id}`, body);
        const updated = res.data.data || res.data;
        setInvoice(prev => ({ ...prev, ...updated }));
      } else {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 30);
        const res = await api.post('/medicalExpenditures', {
          clinicId: selectedClinic._id,
          patientId: task.patientId,
          doctorId: user._id,
          appointments: [task.id],
          dueDate: dueDate.toISOString().slice(0, 10),
          ...body,
        });
        setInvoice(res.data.data || res.data);
      }
    } catch (err) {
      logger.error("Error saving invoice:", err);
    } finally {
      setSavingInvoice(false);
    }
  };

  // Record payment
  const recordPayment = async () => {
    if (!invoice || !payAmount || parseFloat(payAmount) <= 0) return;
    setRecordingPayment(true);
    try {
      const res = await api.post(`/medicalExpenditures/${invoice._id}/payment`, {
        amount: parseFloat(payAmount),
        paymentMethod: payMethod,
        ...(payNote ? { note: payNote } : {}),
      });
      const updated = res.data.data || res.data;
      setInvoice(prev => ({ ...prev, ...updated }));
      setPayAmount("");
      setPayNote("");
    } catch (err) {
      logger.error("Error recording payment:", err);
    } finally {
      setRecordingPayment(false);
    }
  };

  const isBusy = savingAppt || deletingAppt || completingAppt || savingInvoice || recordingPayment;
  const isAlreadyCompleted = selectedAppointment?.status === "COMPLETED";
  const isClosed = invoice?.status === "Cancelled";

  return (
    <>
      {isBusy && (
        <div style={S.loaderOverlay}>
          <Loader message="Please wait…" />
        </div>
      )}

      <div style={S.backdrop} onClick={e => e.target === e.currentTarget && setEditModalOpen(false)}>
        <div style={S.modal}>

          {/* ── Header ── */}
          <div style={S.mhead}>
            <div>
              <div style={S.mheadTitle}>{task.content}</div>
              <div style={S.mheadSub}>
                {invoice
                  ? `Invoice · ${invoice.invoiceNumber || invoice.medicalExpenditureID || ""}`
                  : "No invoice yet"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {invoice && (
                <span style={{ ...S.pill, ...PILL_COLORS[invoice.status] }}>
                  {invoice.status}
                </span>
              )}
              <div style={{ display: "flex", gap: 4 }}>
                {["appointment", "invoice"].map(t => {
                  const disabled = t === "appointment" && !selectedAppointment;
                  return (
                    <button key={t}
                      onClick={() => !disabled && setActiveTab(t)}
                      disabled={disabled}
                      style={{
                        ...S.tabBtn,
                        ...(activeTab === t ? S.tabBtnActive : {}),
                        ...(disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                      }}>
                      {t === "appointment" ? "Appointment" : "Invoice"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── APPOINTMENT TAB ── */}
          {activeTab === "appointment" && (
            <>
              <div style={S.mbody}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
                  <div>
                    <div style={S.sectionTitle}>Date</div>
                    <Calendar year={calYear} month={calMonth} selectedDate={selectedDate}
                      onSelect={handleDateSelect} onPrevMonth={prevMonth} onNextMonth={nextMonth} />
                  </div>
                  <div>
                    <div style={S.sectionTitle}>Time slot</div>
                    <TimeSlots slots={timeSlots} selectedSlot={selectedSlot}
                      onSelect={setSelectedSlot} loading={loadingSlots} />
                    {selectedSlot === null && (
                      <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af" }}>
                        Current: {task.time} · Change date above to pick a new slot
                      </div>
                    )}
                  </div>
                </div>
                <div style={S.formGroup}>
                  <label style={S.flbl}>Appointment type</label>
                  <select value={apptType} onChange={e => handleApptTypeChange(e.target.value)} style={S.fsel}>
                    <option value="">— Select type —</option>
                    {apptTypes.map(t => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={S.mfoot}>
                <button onClick={deleteAppointment} style={S.btnDelete}>Delete appointment</button>
                <div style={{ display: "flex", gap: 8 }}>
                  {!isAlreadyCompleted && (
                    <button onClick={markComplete} disabled={completingAppt} style={{ ...S.btnComplete, opacity: completingAppt ? 0.6 : 1 }}>
                      {completingAppt ? "Completing…" : "✓ Mark Complete"}
                    </button>
                  )}
                  {isAlreadyCompleted && (
                    <span style={S.completedBadge}>✓ Completed</span>
                  )}
                  <button onClick={saveAppointment} style={S.btnConfirm}>Save changes</button>
                </div>
              </div>
            </>
          )}

          {/* ── INVOICE TAB ── */}
          {activeTab === "invoice" && (
            <>
              <div style={S.mbody}>
                {dataLoading ? (
                  <div style={{ textAlign: "center", padding: 40, fontSize: 13, color: "#9ca3af" }}>
                    Loading invoice…
                  </div>
                ) : (
                  <>
                    {/* SKU table */}
                    <div style={S.sectionTitle}>Services &amp; items</div>
                    <div style={S.invSkus}>
                      <div style={S.invHdr}>
                        {["Service / item", "Unit price", "Qty", "Discount %", "Total", ""].map((h, i) => (
                          <span key={i} style={S.invHdrCell}>{h}</span>
                        ))}
                      </div>
                      {skuItems.length === 0
                        ? <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#9ca3af" }}>No items added</div>
                        : skuItems.map((s, i) => (
                          <SkuRow key={s.skuId || i} item={s} index={i}
                            onQtyChange={updateQty} onDiscChange={updateLineDsc} onRemove={removeSku}
                            disabled={isClosed} />
                        ))
                      }
                      {!isClosed && (
                        <div style={S.addSkuRow}>
                          <select value={skuPickerId} onChange={e => setSkuPickerId(e.target.value)} style={S.addSkuSel}>
                            <option value="">— Add a service or product —</option>
                            {skuCatalog
                              .filter(s => !skuItems.find(x => x.skuId === s.clinicSkuId))
                              .map(s => (
                                <option key={s.clinicSkuId} value={s.clinicSkuId}>
                                  {s.name} – {INR(s.unitPrice)}
                                </option>
                              ))
                            }
                          </select>
                          <button onClick={addSku} style={S.btnAddSku}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0f6e56" strokeWidth="2.2" strokeLinecap="round">
                              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add item
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Insurance */}
                    {!isClosed && (
                      <div style={{ ...S.discBox, marginBottom: 14 }}>
                        <div style={S.discBoxTitle}>Insurance</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <span style={{ fontSize: 12, color: "#374151" }}>Apply insurance</span>
                          <Toggle on={insEnabled} onToggle={() => setInsEnabled(v => !v)} />
                        </div>
                        {insEnabled && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <input value={insCompany} onChange={e => setInsCompany(e.target.value)}
                              placeholder="Insurance company" style={{ ...S.finp, height: 30, fontSize: 12 }} />
                            <input value={insPolicy} onChange={e => setInsPolicy(e.target.value)}
                              placeholder="Policy number" style={{ ...S.finp, height: 30, fontSize: 12 }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input type="number" min="0" max="100" value={insCoverage}
                                onChange={e => setInsCoverage(parseFloat(e.target.value) || 0)}
                                style={{ ...S.discInp, flex: 1 }} placeholder="0" />
                              <span style={{ fontSize: 12, color: "#6b7280" }}>% covered</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Totals */}
                    <div style={S.totalsBox}>
                      {[
                        ["Subtotal", INR(totals.subTotal), null],
                        ["Line discounts", "−" + INR(totals.lineDiscTotal), "#16a34a"],
                        ["Insurance coverage", "−" + INR(totals.insAmt), "#16a34a"],
                      ].map(([label, val, col]) => (
                        <div key={label} style={S.totRow}>
                          <span>{label}</span>
                          <span style={col ? { color: col } : {}}>{val}</span>
                        </div>
                      ))}
                      {totals.cgstTotal > 0 && <div style={S.totRow}><span>CGST</span><span>+{INR(totals.cgstTotal)}</span></div>}
                      {totals.sgstTotal > 0 && <div style={S.totRow}><span>SGST</span><span>+{INR(totals.sgstTotal)}</span></div>}
                      {totals.igstTotal > 0 && <div style={S.totRow}><span>IGST</span><span>+{INR(totals.igstTotal)}</span></div>}
                      {totals.taxTotal > 0 && (
                        <div style={{ ...S.totRow, fontStyle: "italic", color: "#6b7280" }}>
                          <span>Total tax</span><span>+{INR(totals.taxTotal)}</span>
                        </div>
                      )}
                      <div style={{ ...S.totRow, ...S.totRowGrand }}>
                        <span>Grand total</span>
                        <span>{INRd(totals.grand)}</span>
                      </div>
                      {invoice && (
                        <>
                          <div style={{ ...S.totRow, color: "#16a34a" }}>
                            <span>Paid</span>
                            <span>−{INRd(totals.paid)}</span>
                          </div>
                          <div style={{ ...S.totRow, fontWeight: 600, color: totals.balance > 0 ? "#b91c1c" : "#16a34a" }}>
                            <span>Balance due</span>
                            <span>{INRd(totals.balance)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Record payment */}
                    {invoice && !isClosed && (
                      <div style={{ ...S.discBox, marginTop: 14 }}>
                        <div style={S.discBoxTitle}>Record payment</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                          <div style={S.formGroup}>
                            <label style={S.flbl}>Amount (₹)</label>
                            <input type="number" min="0" value={payAmount}
                              onChange={e => setPayAmount(e.target.value)}
                              placeholder="0" style={S.finp} />
                          </div>
                          <div style={S.formGroup}>
                            <label style={S.flbl}>Note (optional)</label>
                            <input value={payNote} onChange={e => setPayNote(e.target.value)}
                              placeholder="e.g. Partial via GPay" style={S.finp} />
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 6, marginBottom: 10 }}>
                          {["Cash", "UPI", "Card", "Insurance", "Other"].map(m => (
                            <button key={m} onClick={() => setPayMethod(m)}
                              style={{ ...S.modeBtn, ...(payMethod === m ? S.modeBtnOn : {}) }}>
                              {m}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={recordPayment}
                          disabled={!payAmount || parseFloat(payAmount) <= 0}
                          style={{
                            ...S.btnConfirm, width: "100%",
                            opacity: (!payAmount || parseFloat(payAmount) <= 0) ? 0.5 : 1,
                          }}
                        >
                          Record payment
                        </button>
                      </div>
                    )}

                    {/* Payment history */}
                    {invoice?.paymentHistory?.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={S.sectionTitle}>Payment history</div>
                        {invoice.paymentHistory.map((p, i) => (
                          <div key={i} style={S.historyRow}>
                            <span style={{ fontWeight: 500, color: "#1a2a3a" }}>{INRd(p.amount)}</span>
                            <span style={{ color: "#6b7280" }}>{p.paymentMethod}</span>
                            <span style={{ color: "#9ca3af" }}>
                              {p.date
                                ? new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                                : ""}
                            </span>
                            {p.note && <span style={{ color: "#9ca3af", fontStyle: "italic", gridColumn: "1/-1" }}>{p.note}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div style={S.mfoot}>
                <button onClick={() => setEditModalOpen(false)} style={S.btnBack}>Close</button>
                {!isClosed && !dataLoading && (
                  <button
                    onClick={saveInvoice}
                    disabled={skuItems.length === 0}
                    style={{ ...S.btnNext, opacity: skuItems.length === 0 ? 0.5 : 1 }}
                  >
                    {invoice ? "Update invoice" : "Create invoice"}
                  </button>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default EditBookedAppointment;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  loaderOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" },
  backdrop: { position: "fixed", inset: 0, background: "rgba(26,42,58,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'DM Sans',system-ui,sans-serif" },
  modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, overflow: "hidden", maxHeight: "92vh", overflowY: "auto" },

  mhead: { background: "#1a2a3a", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  mheadTitle: { color: "#fff", fontSize: 15, fontWeight: 600 },
  mheadSub: { color: "#8899aa", fontSize: 11, marginTop: 2 },

  tabBtn: { height: 28, padding: "0 14px", border: "0.5px solid #374151", borderRadius: 7, background: "transparent", color: "#8899aa", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  tabBtnActive: { background: "#f5c842", color: "#1a2a3a", borderColor: "#f5c842", fontWeight: 600 },

  pill: { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 },

  mbody: { padding: "20px 22px" },
  mfoot: { padding: "14px 22px", borderTop: "0.5px solid #e8eaed", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
  sectionTitle: { fontSize: 11, fontWeight: 500, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 10 },

  calBox: { border: "0.5px solid #e8eaed", borderRadius: 10, overflow: "hidden" },
  calNav: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f9fafb", borderBottom: "0.5px solid #e8eaed" },
  calNavBtn: { width: 24, height: 24, border: "0.5px solid #e8eaed", borderRadius: 5, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#374151", fontFamily: "inherit" },
  calGrid: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1, padding: 8 },
  calDayHdr: { fontSize: 10, color: "#9ca3af", textAlign: "center", padding: "3px 0", fontWeight: 500 },
  calDay: { fontSize: 12, textAlign: "center", padding: "5px 3px", borderRadius: 6, cursor: "pointer", color: "#374151" },
  calDayOther: { fontSize: 12, textAlign: "center", padding: "5px 3px", color: "#d1d5db" },
  calDayToday: { background: "#f5c842", color: "#1a2a3a", fontWeight: 600 },
  calDaySelected: { background: "#1d9e75", color: "#fff", fontWeight: 600 },
  calDayDisabled: { color: "#d1d5db", cursor: "default" },

  slotsBox: { border: "0.5px solid #e8eaed", borderRadius: 10, padding: 12 },
  slotsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 },
  slotMsg: { textAlign: "center", padding: "24px 0", fontSize: 12, color: "#9ca3af" },
  slot: { padding: "7px 10px", border: "0.5px solid #e8eaed", borderRadius: 7, fontSize: 12, color: "#374151", cursor: "pointer", textAlign: "center" },
  slotSel: { background: "#1d9e75", color: "#fff", borderColor: "#1d9e75", fontWeight: 500 },

  formGroup: { display: "flex", flexDirection: "column", gap: 4 },
  flbl: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em" },
  finp: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  fsel: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%" },

  invSkus: { border: "0.5px solid #e8eaed", borderRadius: 10, overflow: "hidden", marginBottom: 14 },
  invHdr: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", background: "#f9fafb", borderBottom: "0.5px solid #e8eaed" },
  invHdrCell: { fontSize: 10, color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: ".04em", padding: "8px 10px" },
  skuRow: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", borderBottom: "0.5px solid #f5f5f5", alignItems: "center" },
  skuCell: { padding: "8px 10px", fontSize: 12, color: "#1a2a3a" },
  skuName: { fontSize: 12, fontWeight: 500, color: "#1a2a3a" },
  skuCat: { fontSize: 10, color: "#9ca3af" },
  numInp: { height: 28, border: "0.5px solid #e8eaed", borderRadius: 5, padding: "0 6px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", textAlign: "center" },
  delBtn: { width: 22, height: 22, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 },
  addSkuRow: { padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, borderTop: "0.5px solid #e8eaed", background: "#fafafa" },
  addSkuSel: { flex: 1, height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", fontFamily: "inherit" },
  btnAddSku: { height: 30, padding: "0 12px", border: "0.5px solid #1d9e75", borderRadius: 6, background: "#e1f5ee", color: "#0f6e56", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" },

  discBox: { border: "0.5px solid #e8eaed", borderRadius: 10, padding: 12 },
  discBoxTitle: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 },
  modeBtn: { height: 28, border: "0.5px solid #e8eaed", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff", color: "#6b7280" },
  modeBtnOn: { background: "#1a2a3a", color: "#fff", borderColor: "#1a2a3a" },
  discInp: { height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit" },

  togTrack: { width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", position: "relative", transition: "background .2s" },
  togThumb: { position: "absolute", top: 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s" },

  totalsBox: { background: "#f9fafb", border: "0.5px solid #e8eaed", borderRadius: 10, padding: "12px 14px" },
  totRow: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151", padding: "3px 0" },
  totRowGrand: { borderTop: "0.5px solid #e8eaed", marginTop: 6, paddingTop: 8, fontSize: 14, fontWeight: 600, color: "#1a2a3a" },

  historyRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, padding: "7px 0", borderBottom: "0.5px solid #f5f5f5", fontSize: 12 },

  btnBack: { height: 36, padding: "0 18px", border: "0.5px solid #e8eaed", borderRadius: 8, background: "#fff", fontSize: 13, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
  btnNext: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1d9e75", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  btnConfirm: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1a2a3a", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  btnDelete: { height: 36, padding: "0 18px", border: "none", borderRadius: 8, background: "#fee2e2", color: "#b91c1c", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  btnComplete: { height: 36, padding: "0 18px", border: "none", borderRadius: 8, background: "#dcfce7", color: "#166534", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  completedBadge: { height: 36, padding: "0 14px", display: "inline-flex", alignItems: "center", borderRadius: 8, background: "#dcfce7", color: "#166534", fontSize: 13, fontWeight: 500 },
};
